/**
 * The one shape /compare renders a product in, and the one place the
 * `/api/products/[slug]` payload is translated into it.
 *
 * WHY THIS FILE EXISTS (FRIDGE-872). The compare client used to do
 * `return response.json()` and hand the result straight to the table as if it
 * were the product. It is not: the endpoint answers `{ product: { … } }`, and
 * its field names are the camelCase display payload (`capacity`,
 * `energyUsage`, `dimensions.width`, `canonicalUrl`, `seoSlug`), not the
 * snake_case column names the table asked for (`capacity_cuft`,
 * `energy_kwh_yr`, `width_inches`, `canonical_url`, `slug`). Every cell in the
 * loaded comparison therefore read "—" and every product header rendered an
 * empty title, on every visit, for as long as the tool has existed. Mapping it
 * once, here, is what makes the rebuilt page show real numbers.
 *
 * PURE — no React, no fetch. `tests/compare-product-mapping.test.ts` pins it
 * against the payload the endpoint actually returns.
 */
import { finFromProductHref } from '@/lib/card-link-names'

export interface CompareOffer {
  retailer: string
  priceCents: number
  inStock: boolean
}

export interface CompareRow {
  /** The token this row was requested by — the `/compare?products=` slug, which is also its remove key. */
  slug: string
  id: string
  /** The FIN the product URL encodes; null when the slug carries no minted number. */
  fin: string | null
  brand: string
  title: string
  model: string | null
  href: string
  image: string | null
  priceCents: number | null
  /** Retailer ladder, cheapest first, network names removed. */
  offers: CompareOffer[]
  capacityCuft: number | null
  energyKwhYr: number | null
  widthIn: number | null
  heightIn: number | null
  depthIn: number | null
  weightLbs: number | null
  energyStar: boolean | null
  productType: string | null
  color: string | null
}

/**
 * WHY THERE IS NO noiseDb, rating OR reviewsCount HERE, measured 09/05/2026.
 *
 * The table this file feeds carried a "Noise level", a "Rating" and a
 * "Reviews" row, each read out of the `specs_json` blob. Counted over the
 * 5,708 products that are `status='active' and pdp_eligible` — the population
 * /compare can actually show — the number of products carrying any of those
 * keys is ZERO:
 *
 *   specs_json ? 'noise_db'      →  0        specs_json ? 'rating'         →  0
 *   specs_json ? 'noise_level'   →  0        specs_json ? 'reviews'        →  0
 *                                            specs_json ? 'reviews_count'  →  0
 *
 * `products` has no `rating`, `reviews_count` or `energy_star` column at all,
 * so no amount of mapping reaches them. `products.noise_db` does exist and is
 * even selected by lib/products/get-product-detail.ts, but its formatter drops
 * it before the payload leaves, and only 85 of the 5,708 (1.5%) carry a value
 * anyway — far too thin to earn a permanent column in a four-way comparison,
 * where a highlight needs TWO products to have the number.
 *
 * Those three rows could therefore never render anything but an em dash, for
 * any product on the site, and two of them fed best-value highlights that
 * could never fire. They are gone rather than shipped blank.
 *
 * KEPT, because they are real: `energyStar` (247 products carry
 * `specs_json.energy_star`) and `energyKwhYr` (730 carry `energy_kwh_yr`).
 * ADDED: `weightLbs` — the payload has carried `weight` all along and nothing
 * rendered it, and at 1,525 products it is better covered than capacity.
 */

/**
 * Affiliate networks are plumbing, never a shop a visitor can walk into. The
 * endpoint substitutes the advertiser name where it knows one and otherwise
 * leaves the network's own name in `retailer`; those rows are dropped from the
 * ladder rather than printed as if a shopper could buy there.
 */
const NON_RETAILERS = new Set(['cj affiliate', 'flexoffers', 'shareasale', 'unknown', ''])

function num(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

/** `/api/products/[slug]` answers `{ product: … }`; a bare product object is accepted too. */
export function toCompareRow(payload: unknown, requestedSlug: string): CompareRow | null {
  const envelope = (payload ?? {}) as Record<string, unknown>
  const raw = (envelope.product ?? envelope) as Record<string, unknown>
  const id = str(raw.id)
  if (!id) return null

  const seoSlug = str(raw.seoSlug)
  const href = str(raw.canonicalUrl) ?? (seoSlug ? `/products/${seoSlug}` : '')
  const specs = (raw.specs ?? raw.specs_json ?? {}) as Record<string, unknown>
  const dimensions = (raw.dimensions ?? {}) as Record<string, unknown>
  const images = Array.isArray(raw.images) ? (raw.images as unknown[]) : []

  const offers = (Array.isArray(raw.offers) ? (raw.offers as Record<string, unknown>[]) : [])
    .map((offer) => ({
      retailer: str(offer.retailer) ?? '',
      priceCents: num(offer.price) ?? 0,
      inStock: offer.inStock === true,
    }))
    .filter((offer) => offer.priceCents > 0 && !NON_RETAILERS.has(offer.retailer.toLowerCase()))
    .sort((a, b) => a.priceCents - b.priceCents)

  const model = str(raw.model)
  return {
    slug: requestedSlug,
    id,
    fin: finFromProductHref(href),
    brand: str(raw.brand) ?? '',
    title: str(raw.title) ?? '',
    model: model && model.length <= 24 ? model : null,
    href: href || `/products/${requestedSlug}`,
    image: str(images[0]),
    priceCents: num(raw.lowest_price_cents),
    offers,
    capacityCuft: num(raw.capacity),
    energyKwhYr: num(raw.energyUsage),
    widthIn: num(dimensions.width),
    heightIn: num(dimensions.height),
    depthIn: num(dimensions.depth),
    weightLbs: num(raw.weight),
    energyStar: specs.energy_star === true ? true : specs.energy_star === false ? false : null,
    productType: str(raw.type),
    color: str(raw.color),
  }
}

/** The `/compare?products=` token for a site path — the last non-empty segment. */
export function slugFromHref(href: string | null | undefined): string {
  if (!href) return ''
  return href.split('?')[0].split('#')[0].split('/').filter(Boolean).pop() ?? ''
}
